/**
 * PersonaPicker — the demo persona list, shown in the "Switch persona" popover on
 * the acting-as banner. Two views (same as the original top-bar switcher):
 *   - People — a flat list of everyone in the active company's cast (default).
 *   - Roles  — grouped by worker type (Hourly / Salary / Manager / Auditor / Admin…),
 *              each section collapsible; the active persona's section opens by default.
 * Picking a persona swaps the whole profile (employee + access role) and lands on
 * the timesheet. The always-on reconcile effects live in usePersonaReconcile.
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronRight } from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { useTimesheetStore } from '@/modules/timesheet/stores/timesheetStore'
import { timesheetDataService } from '@/modules/timesheet/services/timesheetDataService'
import { scopeDemoCast } from '@/modules/timesheet/lib/demoCast'
import { getDemoCompany } from '@/types/demoCompanies'
import { cn } from '@/lib/utils'
import type { TsEmployee } from '@/modules/timesheet/types/timesheet.types'
import { personaTag, applyPersona, SECTIONS, workerSection } from './personaCast'

function PersonaRow({
  emp, isActive, indent, onPick,
}: { emp: TsEmployee; isActive: boolean; indent?: boolean; onPick: (emp: TsEmployee) => void }) {
  return (
    <button
      onClick={() => onPick(emp)}
      className={cn(
        'w-full text-left pr-3 py-2 flex items-center gap-2.5 transition-colors',
        indent ? 'pl-9' : 'pl-3',
        isActive ? 'bg-brand-50' : 'hover:bg-surface-muted',
      )}
    >
      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700 text-[11px] font-semibold">
        {emp.initials}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-body-sm font-medium text-text-primary truncate">{emp.name}</span>
        <span className="mt-0.5 block">
          <span className="inline-block text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-surface-muted text-text-secondary border border-border">{personaTag(emp)}</span>
        </span>
        <span className="block text-[10px] text-text-tertiary truncate leading-snug">{emp.role}</span>
      </span>
      {isActive && <Check size={14} className="text-brand-600 flex-shrink-0" />}
    </button>
  )
}

export function PersonaPicker({ onPicked }: { onPicked?: () => void }) {
  const appMode = useAppStore((s) => s.appMode)
  // Re-render when the Supabase roster lands / the company changes.
  const referenceDataVersion = useAppStore((s) => s.referenceDataVersion)
  void referenceDataVersion
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const currentEmployeeId = useTimesheetStore((s) => s.currentEmployeeId)
  const navigate = useNavigate()
  const [tab, setTab] = useState<'people' | 'roles'>('people')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Expand the active persona's section when the picker opens / persona changes.
  useEffect(() => {
    const emp = currentEmployeeId ? timesheetDataService.getEmployee(currentEmployeeId) : undefined
    setExpanded(new Set(emp ? [workerSection(emp)] : []))
  }, [currentEmployeeId])

  if (appMode !== 'demo') return null

  const allEmployees = timesheetDataService.getEmployees()
  const personas = scopeDemoCast(allEmployees, currentCompanyId)
    .sort((a, b) => (a.pernr ?? a.id).localeCompare(b.pernr ?? b.id))
  const peopleSorted = [...personas].sort((a, b) => a.name.localeCompare(b.name))
  const pickedCompany = getDemoCompany(currentCompanyId)
  const emptyCastForCompany = personas.length === 0 && allEmployees.length > 0 && !!pickedCompany?.castOrgId

  function pick(emp: TsEmployee) {
    applyPersona(emp, { navigate })
    onPicked?.()
  }
  function toggleSection(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Bucket personas by worker-type section for the grouped (Roles) view.
  const bySection = new Map<string, TsEmployee[]>()
  for (const emp of personas) {
    const k = workerSection(emp)
    const arr = bySection.get(k)
    if (arr) arr.push(emp)
    else bySection.set(k, [emp])
  }

  if (personas.length === 0) {
    return (
      <div className="px-3 py-3 text-[12px] text-text-tertiary">
        {emptyCastForCompany
          ? `No timesheet personas for ${pickedCompany?.name ?? 'this company'} yet.`
          : 'Personas are still loading — reopen this menu in a moment.'}
      </div>
    )
  }

  return (
    <div>
      {/* People (flat) / Roles (grouped) toggle */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('people')}
          className={cn('flex-1 rounded-md px-2 py-1 text-[12px] font-medium transition-colors', tab === 'people' ? 'bg-brand-50 text-brand-700' : 'text-text-secondary hover:bg-surface-muted')}
        >
          People
        </button>
        <button
          type="button"
          onClick={() => setTab('roles')}
          className={cn('flex-1 rounded-md px-2 py-1 text-[12px] font-medium transition-colors', tab === 'roles' ? 'bg-brand-50 text-brand-700' : 'text-text-secondary hover:bg-surface-muted')}
        >
          Roles
        </button>
      </div>

      <div className="max-h-[44vh] overflow-y-auto">
        {tab === 'people' ? (
          peopleSorted.map((emp) => (
            <PersonaRow key={emp.id} emp={emp} isActive={emp.id === currentEmployeeId} onPick={pick} />
          ))
        ) : (
          SECTIONS.map((sec) => {
            const members = bySection.get(sec.key)
            if (!members || members.length === 0) return null
            const isExp = expanded.has(sec.key)
            const hasActive = members.some((m) => m.id === currentEmployeeId)
            return (
              <div key={sec.key} className="border-t border-border first:border-t-0">
                <button
                  type="button"
                  onClick={() => toggleSection(sec.key)}
                  className="w-full px-3 py-2 flex items-center gap-2 hover:bg-surface-muted/60 transition-colors"
                  aria-expanded={isExp}
                >
                  <ChevronRight size={14} className={cn('flex-shrink-0 text-text-tertiary transition-transform', isExp && 'rotate-90')} />
                  <span className="min-w-0 flex-1 text-left">
                    <span className="flex items-center gap-1.5">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-text-secondary">{sec.label}</span>
                      {hasActive && <span className="h-1.5 w-1.5 rounded-full bg-brand-500 flex-shrink-0" title="Active persona is here" />}
                    </span>
                    <span className="block text-[10px] text-text-tertiary leading-snug truncate">{sec.hint}</span>
                  </span>
                  <span className="text-[10px] text-text-tertiary tabular-nums flex-shrink-0">{members.length}</span>
                </button>
                {isExp && members.map((emp) => (
                  <PersonaRow key={emp.id} emp={emp} isActive={emp.id === currentEmployeeId} indent onPick={pick} />
                ))}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
